"use client";

import { ViewTransition, type ReactNode } from "react";
import { PhoneTabBar } from "@/components/shell/PhoneTabBar";
import { DesktopHeader } from "@/components/shell/DesktopHeader";
import { CookieBanner } from "@/components/shell/CookieBanner";
import { ToastHost } from "@/lib/toast";
import { useNavigation } from "@/lib/navigation";
import { LoginHost } from "@/components/auth/login";
import { RegistrationHost } from "@/components/registration/registration";
import { WaitingListHost } from "@/components/waiting/waitingList";
import { LiveTrailStrip } from "@/components/live/LiveTrailStrip";
import { PreviewBadge } from "@/components/shell/PreviewBadge";
import { LiveTrailHost } from "@/components/live/LiveTrailSheet";
import { NotificationsAutoAsk, NotificationsHost } from "@/components/notifications/notifications";
import { NewsAsk } from "@/components/profile/NewsAsk";
import { LookedAroundTracker } from "@/lib/lookedAround";
import { NotificationsSync } from "@/components/notifications/NotificationsSync";

/**
 * The frame around every page: the live trail strip above everything on the day
 * of an event, the desktop's top bar or the phone's tabs, the hosts for the
 * sheets that open over any page (sign-in, registration, the live trail,
 * toasts, the cookie banner), and the view transition a navigation plays -- a page
 * pushed over a tab slides in, going back slides it out, a tab change
 * crossfades (globals.css).
 */
const TRANSITION_CLASS = { push: "page-push", pop: "page-pop", tab: "tab-switch", none: "none" } as const;

export function AppShell({ children }: { children: ReactNode }) {
  const { kind } = useNavigation();

  return (
    <>
      <LiveTrailStrip />
      <DesktopHeader />
      <ViewTransition default={TRANSITION_CLASS[kind]}>
        <div
          // room under the page for the phone's tab bar, which a desktop does not have
          className="relative mx-auto min-h-dvh w-full pb-[calc(var(--tabbar-h)+var(--safe-bottom))] desk:min-h-[calc(100dvh-4rem)] desk:pb-0"
          id="page"
        >
          {children}
        </div>
      </ViewTransition>
      <PhoneTabBar />
      <LoginHost />
      <RegistrationHost />
      <WaitingListHost />
      <LiveTrailHost />
      <NotificationsHost />
      {/* the app's own questions wait until the client has looked around (lib/lookedAround.ts) */}
      <LookedAroundTracker />
      <NotificationsAutoAsk />
      {/* admin-api#51: the question about news by email, on a later visit than the one above */}
      <NewsAsk />
      <NotificationsSync />
      <CookieBanner />
      <ToastHost />
      <PreviewBadge />
    </>
  );
}
